import { Caption, useStyles } from '@archipro-design/aria';
import { Link, useSearchParams } from 'react-router';
import * as S from './LoginPage.style';
import type { LoginSignUpProps } from '../component/login-component/LoginSignUp';
import LoginSignUp from '../component/login-component/LoginSignUp';
import { PolicyLink } from '../component/policy-link/PolicyLink';
import { LogoAnimated } from '../../../../../rocco/src/components/logo-animated/LogoAnimated';
import { decodeLoginRedirect } from '~/modules/root';
import { AuthPageCard } from '~/modules-rocco/user/component/auth-modal/AuthPageCard';
import { getImageAttrs } from '~/modules/images';
import { useRevampedAuthSurface } from '../hook/use-revamped-auth-surface';
import { useHeroMarket } from '~/ui-rocco/pages/home-page/sections/hero-markets';

/**
 * The landscape brand photograph, as a path the image service can resolve.
 * Desktop only — below 768px the market's portrait hero takes over.
 */
const LOGIN_HERO_PATH = 'assets/website/images/ui/home-hero-image-mini.png';

interface LoginPageProps {
    /**
     * Optional because the `/login` route renders this as its default export,
     * with no props at all. Only the 401 page supplies anything.
     */
    login?: Partial<LoginSignUpProps>;
}

export const LoginPage = (props?: LoginPageProps) => {
    const { css } = useStyles();
    const { mobileImage } = useHeroMarket();
    const newAuthEnabled = useRevampedAuthSurface();

    const [searchParams] = useSearchParams();

    const skipPostSignUpRedirect =
        !!props?.login?.skipPostSignUpRedirect ||
        searchParams.get('skipPostSignUp') === '1';

    const redirect = decodeLoginRedirect(searchParams.get('redirect'));

    return (
        <div className={css(S.PageWrapper)}>
            {/* Art direction, not resolution. The desktop photograph is a
                1.52:1 landscape; a phone screen is 0.46:1. `cover` on that
                pair shows a ~30%-wide column out of the middle of the frame,
                which on this image is flat brown — so the top of the page read
                as a colour block no matter which derivative was served.
                Serving a bigger file only sharpened it.

                The market's own portrait hero is the fix, and it already
                exists: the homepage hero is art-directed per market and ships
                a ~9:16 mobile crop beside its landscape one. A `<picture>`
                rather than two `<img>`s, so the browser resolves exactly one
                — React's SSR preloads every non-lazy `<img>` it renders, and
                a hidden second copy would be fetched in full. */}
            <picture>
                {/* Shape, not width. The crop is wrong whenever the viewport
                    is taller than it is wide, which is not a phone-sized
                    question: a 768x1024 tablet showed 48% of the landscape
                    frame, a 744px one 52%. Keyed on the aspect ratio, every
                    portrait viewport gets the portrait photograph and no
                    breakpoint has to be kept in step with the layout's. */}
                <source media="(max-aspect-ratio: 1/1)" srcSet={mobileImage} />
                <img
                    {...getImageAttrs(LOGIN_HERO_PATH, { sizes: '100vw' })}
                    alt=""
                    className="absolute inset-0 size-full object-cover"
                />
            </picture>
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    width: '100%',
                    height: '100%',
                }}
            ></div>
            <div className={css(S.ContentWrap)}>
                {newAuthEnabled ? (
                    // The card carries its own brand pane with the ArchiPro
                    // mark, so the standalone logo would be the second one on
                    // screen. The backdrop and the policy row stay: they are the
                    // page's identity, and only the body was ever a duplicate of
                    // the modal.
                    //
                    // Below 744px the column is the whole viewport with the
                    // card pushed to its foot, so the card reads as the bottom
                    // sheet a phone gets from every other gate, and the
                    // backdrop stands in for the page such a sheet would
                    // normally sit over. `min-h`, not `h`: a long step grows
                    // the page and scrolls, rather than pushing its own
                    // heading off the top of the screen.
                    //
                    // 744px, not `md`, because that is where `ContentWrap`
                    // above becomes an absolutely filled 100dvh column with a
                    // padded top and a visible policy row. Holding `min-h-dvh`
                    // past that point asks for the whole viewport inside a box
                    // that is already the whole viewport minus those two, and
                    // the surplus overflows a backdrop pinned at 100dvh that
                    // cannot follow it. iPad mini portrait is exactly 744px.
                    <div className="relative flex min-h-dvh w-full flex-col justify-end min-[744px]:min-h-0 min-[744px]:flex-1 min-[744px]:flex-row min-[744px]:items-center min-[744px]:justify-center min-[744px]:max-md:p-4 md:p-6">
                        {/* The card's brand pane is only drawn from md, so
                            below it this page carried no ArchiPro mark and no
                            way off itself: the whole interactive inventory was
                            the four sign-in buttons and three footer links, and
                            the browser's own Back was the only exit. `mb-auto`
                            keeps it at the head of the column while the card
                            stays at the foot. `self-start` keeps the
                            anchor the size of the mark: a flex column stretches
                            its children, which turned a wordmark-sized link into
                            an invisible full-width strip that left the page on any
                            tap near the top edge.

                            At 744px the column becomes a centred row while the
                            card's own brand pane is still absent until md, so
                            the mark is still needed but has nowhere sensible to
                            sit in the flow — it rendered as a stray item beside
                            the card. Taken out of the flow for that band
                            instead, at the page's top corner where it reads as
                            the mark rather than as content. */}
                        <Link
                            to="/"
                            aria-label="ArchiPro home"
                            className="mb-auto inline-flex self-start px-4 pt-6 min-[744px]:absolute min-[744px]:top-0 min-[744px]:left-0 md:hidden"
                        >
                            <LogoAnimated
                                size={4}
                                mode="disabled"
                                color="light"
                            />
                        </Link>
                        <AuthPageCard
                            authSource="loginPage"
                            redirect={redirect}
                            skipPostSignUp={skipPostSignUpRedirect}
                            // The 401 page states why the reader was sent
                            // here. `subtitle`, not `message`: message only
                            // renders on the details step, so the reason would
                            // be invisible on the screen they actually land on.
                            // As the subtitle it shows on both, because the
                            // details step falls back to it.
                            subtitle={props?.login?.welcomeMessage || undefined}
                            defaultPublicUser={props?.login?.defaultPublicUser}
                            // Sheet below 744px: flush with the foot of the
                            // screen, rounded only along the edge that meets
                            // the backdrop, at the drawer's own radius, so the
                            // two surfaces are the same shape whichever one the
                            // reader arrives at.
                            //
                            // From 744px it is a card again, and capped: the
                            // brand pane only appears at `md`, so an uncapped
                            // card in between is a full-width white band around
                            // a 363px column.
                            className="max-[743.98px]:rounded-t-2xl max-[743.98px]:rounded-b-none min-[744px]:max-md:max-w-135"
                        />
                    </div>
                ) : (
                    <div className={css(S.Content)}>
                        <Link className={css(S.LogoWrapper)} to="/">
                            <LogoAnimated
                                size={4}
                                mode="disabled"
                                color="light"
                            />
                        </Link>

                        <div className={css(S.LoginWrapper)}>
                            <LoginSignUp
                                {...props?.login}
                                authSource="loginPage"
                                skipPostSignUpRedirect={skipPostSignUpRedirect}
                                redirect={redirect}
                            />
                        </div>
                    </div>
                )}

                <div className={css(S.Links)}>
                    <PolicyLink as="a" />
                    <Caption variant="02" as={Link} to="/become-a-pro">
                        ArchiPro for Business
                    </Caption>
                    <Caption variant="02" as="a" href="/contact-us">
                        Contact Us
                    </Caption>
                </div>
            </div>
        </div>
    );
};
